import API_BASE_URL from '../config';
import { fetchWithAuth } from '../utils/fetchWithAuth';

export interface NotificationItem {
  entity_id: string;
  entity_type: 'comment' | 'resolution' | 'comment_mention';
  created_at: string;
  seen?: boolean;
  hazard_id?: string;
}

export interface NotificationsResponse {
  items: NotificationItem[];
  unreadCount: number;
}

export interface UnreadCountResponse {
  unreadCount: number;
}

async function fetchUnreadCount(): Promise<number> {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/notifications/unreadCount`, {
    method: 'GET',
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch unread notification count (${res.status})`);
  }
  const data = (await res.json()) as UnreadCountResponse;
  return data.unreadCount || 0;
}

async function fetchNotifications(): Promise<NotificationsResponse> {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/notifications`, {
    method: 'GET',
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch notifications (${res.status})`);
  }
  return (await res.json()) as NotificationsResponse;
}

async function markAllRead(): Promise<{ ok: boolean; lastSeen?: string }> {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/notifications/markRead`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    throw new Error(`Failed to mark notifications as read (${res.status})`);
  }
  return (await res.json()) as { ok: boolean; lastSeen?: string };
}

async function clearAll(): Promise<{ ok: boolean }> {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/notifications/clear`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    throw new Error(`Failed to clear notifications (${res.status})`);
  }
  return (await res.json()) as { ok: boolean };
}

async function markAsRead(entityId: string, entityType: string): Promise<{ ok: boolean }> {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/notifications/markReadSingle`, {
    method: 'POST',
    body: JSON.stringify({
      entity_id: entityId,
      entity_type: entityType,
    }),
  });
  if (!res.ok) {
    throw new Error(`Failed to mark notification as read (${res.status})`);
  }
  return (await res.json()) as { ok: boolean };
}

async function deleteNotification(entityId: string, entityType: string): Promise<{ ok: boolean }> {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/notifications/delete`, {
    method: 'POST',
    body: JSON.stringify({
      entity_id: entityId,
      entity_type: entityType,
    }),
  });
  if (!res.ok) {
    throw new Error(`Failed to delete notification (${res.status})`);
  }
  return (await res.json()) as { ok: boolean };
}

const notificationService = {
  fetchUnreadCount,
  fetchNotifications,
  markAllRead,
  markAsRead,
  clearAll,
  deleteNotification,
};

export default notificationService;


